import { useMemo } from "react"
import { Feature } from "shared/feature"
import { FeatureAssignment } from "shared/featureAssignment"
import {
  canUseFeature as resolveCanUseFeature,
  readDefaultActivated,
  readFeatureRefName,
  resolveFeatureAccessState,
  resolveFeatureActivatedAtMs,
} from "shared/featureResolution"
import {
  useGetFeaturesAssignementQuery,
  useGetFeaturesQuery,
} from "./thingsApi"

/**
 * useFeatures
 * Fetches feature assignments for a tenant and exposes:
 *  - activatedFeatures: string[] (NOTE: name kept exactly as requested, typo preserved intentionally)
 *  - canUseFeature(refName: string): boolean
 *
 * Precedence is `assignment ?? default`, resolved through
 * `shared/featureResolution` — the same module the backend's
 * `canTenantUseFeature` calls, so what the UI shows and what enforcement
 * allows cannot disagree. Until LOCKER-408 this took the UNION of activated
 * assignments and default-activated features, which meant a feature a tenant
 * had been explicitly turned off for still rendered as available.
 *
 * @param tenantId string | undefined
 */
export const useFeatures = (tenantId?: string) => {
  const skip = !tenantId

  const { data } = useGetFeaturesAssignementQuery(
    { params: { tenantId: tenantId || "" } },
    { skip },
  )

  // fetch features
  const qF = useGetFeaturesQuery(
    { params: { tenantId: tenantId || "" } },
    { skip },
  )

  /**
   * refName -> the tenant's assignment for it. Built from the assignment list
   * rather than the feature list because an assignment can exist for a feature
   * the features query did not return.
   */
  const assignmentsByRefName = useMemo(() => {
    const things = (data?.things || []) as FeatureAssignment[]
    const map: Record<string, FeatureAssignment> = {}
    things.forEach((fa) => {
      if (typeof fa?.featureRefName === "string") map[fa.featureRefName] = fa
    })
    return map
  }, [data])

  /** refName -> the Feature thing itself. */
  const featuresByRefName = useMemo(() => {
    const things = (qF.data?.things || []) as Feature[]
    const map: Record<string, Feature> = {}
    things.forEach((f) => {
      const ref = readFeatureRefName(f)
      if (typeof ref === "string") map[ref] = f
    })
    return map
  }, [qF.data])

  /** refName -> the feature's own defaultActivated flag. */
  const defaultsByRefName = useMemo(() => {
    const map: Record<string, boolean> = {}
    Object.entries(featuresByRefName).forEach(([ref, f]) => {
      map[ref] = readDefaultActivated(f)
    })
    return map
  }, [featuresByRefName])

  const canUseFeature = (refName: string) =>
    resolveCanUseFeature(
      assignmentsByRefName[refName],
      defaultsByRefName[refName],
    )

  /**
   * Every refName that resolves to granted — the union of both lists' refNames
   * put through the precedence rule, so a deactivated assignment removes a
   * default-activated feature from this list instead of being ignored.
   */
  const activatedFeatures = useMemo(() => {
    const refNames = new Set<string>([
      ...Object.keys(assignmentsByRefName),
      ...Object.keys(defaultsByRefName),
    ])
    return Array.from(refNames).filter((ref) =>
      resolveCanUseFeature(assignmentsByRefName[ref], defaultsByRefName[ref]),
    )
  }, [assignmentsByRefName, defaultsByRefName])

  /**
   * When the feature took effect for this tenant, epoch ms, or `undefined` when
   * it is not granted or no instant is known. Same rule as the server's
   * `resolveTenantFeatureActivations`, via `shared/featureResolution`.
   */
  const activatedAtMs = (refName: string) =>
    resolveFeatureActivatedAtMs(
      resolveFeatureAccessState(
        assignmentsByRefName[refName],
        defaultsByRefName[refName],
      ),
      assignmentsByRefName[refName] as any,
      featuresByRefName[refName] as any,
    )

  return { activatedFeatures, canUseFeature, activatedAtMs }
}

export default useFeatures
